import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { Globals } from '../global/globals';
import { UserInfo } from '../dtos/user-info';
import { ProfileUpdateRequest } from '../dtos/profile-update-request';
import { ChangePasswordRequest } from '../dtos/change-password-request';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private readonly profileUri = this.globals.backendUri + '/users/me';
  private readonly currentUserSubject = new BehaviorSubject<UserInfo | null>(null);
  // Public observable that emits current user updates (including cleared on logout)
  public readonly currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private globals: Globals,
  ) {}

  getCurrentUser(forceRefresh = false): Observable<UserInfo> {
    const cachedUser = this.currentUserSubject.value;
    if (cachedUser && !forceRefresh) {
      return of(cachedUser);
    }

    return this.httpClient.get<any>(this.profileUri).pipe(
      map(
        (res) =>
          new UserInfo(
            res.id || undefined,
            res.email || '',
            res.firstName || '',
            res.lastName || '',
            this.mapRole(res),
            res.affiliation,
            res.uiTheme,
          ),
      ),
      tap((user) => this.currentUserSubject.next(user)),
    );
  }

  updateCurrentUser(request: ProfileUpdateRequest): Observable<UserInfo> {
    return this.httpClient.put<any>(this.profileUri, request).pipe(
      map(
        (res) =>
          new UserInfo(
            res.id || this.currentUserSubject.value?.id || undefined,
            res.email || request.email,
            res.firstName || request.firstName,
            res.lastName || request.lastName,
            this.mapRole(res, this.currentUserSubject.value?.role),
            res.affiliation || request.affiliation,
            res.uiTheme || request.uiTheme,
          ),
      ),
      tap((user) => this.currentUserSubject.next(user)),
    );
  }

  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.httpClient.put<void>(`${this.profileUri}/change-password`, request);
  }

  deactivateCurrentUser(): Observable<void> {
    return this.httpClient.delete<void>(this.profileUri).pipe(tap(() => this.clearCurrentUser()));
  }

  /**
   * Clear the locally cached current user. Useful when the user logs out.
   */
  clearCurrentUser(): void {
    this.currentUserSubject.next(null);
  }

  getCurrentUserSnapshot(): UserInfo | null {
    return this.currentUserSubject.value;
  }

  private mapRole(user: any, fallback = 'UNDEFINED'): string {
    if (user?.role) {
      return String(user.role).replace(/^ROLE_/, '');
    }

    const roles = Array.isArray(user?.roles) ? user.roles : [];

    if (roles.some((role: string) => role === 'ROLE_SUPER_ADMIN' || role === 'SUPER_ADMIN')) {
      return 'SUPER_ADMIN';
    }
    if (roles.some((role: string) => role === 'ROLE_ADMIN' || role === 'ADMIN')) {
      return 'ADMIN';
    }
    if (roles.some((role: string) => role === 'ROLE_USER' || role === 'USER')) {
      return 'USER';
    }

    return fallback;
  }
}
